import { z } from "zod";

const identifierPattern = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const hmiIdentifierSchema = z.string().trim().min(1).max(128).regex(identifierPattern, {
  message: "Identifier must start with a letter or underscore and contain only letters, digits, or underscores.",
});

export const hmiLiteralValueSchema = z.union([z.number(), z.string(), z.boolean(), z.null()]);

const expressionBaseSchema = z.object({
  id: z.string().trim().min(1).max(128),
});

export const hmiLiteralExpressionSchema = expressionBaseSchema.extend({
  kind: z.literal("literal"),
  value: hmiLiteralValueSchema,
});

export const hmiIdentifierExpressionSchema = expressionBaseSchema.extend({
  kind: z.literal("identifier"),
  name: hmiIdentifierSchema,
});

const unaryOperatorSchema = z.enum(["not", "neg"]);
const binaryOperatorSchema = z.enum([
  "add",
  "sub",
  "mul",
  "div",
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "and",
  "or",
]);
const functionNameSchema = z.enum(["min", "max", "avg", "coalesce", "clamp", "abs", "round"]);

export type HmiExpression =
  | z.infer<typeof hmiLiteralExpressionSchema>
  | z.infer<typeof hmiIdentifierExpressionSchema>
  | {
      id: string;
      kind: "unary";
      op: z.infer<typeof unaryOperatorSchema>;
      input: HmiExpression;
    }
  | {
      id: string;
      kind: "binary";
      op: z.infer<typeof binaryOperatorSchema>;
      left: HmiExpression;
      right: HmiExpression;
    }
  | {
      id: string;
      kind: "call";
      fn: z.infer<typeof functionNameSchema>;
      args: HmiExpression[];
    };

export const hmiExpressionSchema: z.ZodType<HmiExpression> = z.lazy(() =>
  z.union([
    hmiLiteralExpressionSchema,
    hmiIdentifierExpressionSchema,
    expressionBaseSchema.extend({
      kind: z.literal("unary"),
      op: unaryOperatorSchema,
      input: hmiExpressionSchema,
    }),
    expressionBaseSchema.extend({
      kind: z.literal("binary"),
      op: binaryOperatorSchema,
      left: hmiExpressionSchema,
      right: hmiExpressionSchema,
    }),
    expressionBaseSchema.extend({
      kind: z.literal("call"),
      fn: functionNameSchema,
      args: z.array(hmiExpressionSchema).min(1).max(8),
    }),
  ]),
);

export type HmiExpressionRule =
  | {
      id: string;
      type: "set_style";
      target: "fill" | "stroke" | "opacity" | "text" | "visibility";
      when: HmiExpression;
      value: HmiExpression;
      elseValue?: HmiExpression | null;
    }
  | {
      id: string;
      type: "set_status";
      when: HmiExpression;
      value: HmiExpression;
    };

export const hmiExpressionRuleSchema: z.ZodType<HmiExpressionRule> = z.union([
  z.object({
    id: z.string().trim().min(1).max(128),
    type: z.literal("set_style"),
    target: z.enum(["fill", "stroke", "opacity", "text", "visibility"]),
    when: hmiExpressionSchema,
    value: hmiExpressionSchema,
    elseValue: hmiExpressionSchema.nullable().optional(),
  }),
  z.object({
    id: z.string().trim().min(1).max(128),
    type: z.literal("set_status"),
    when: hmiExpressionSchema,
    value: hmiExpressionSchema,
  }),
]);
